import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Hook for downloading photos with loading state management
 * Handles fetching photo data, creating blob URL, and triggering download
 */
export function usePhotoDownload() {
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Download a photo by ID
   * @param photoId - The ID of the photo to download
   * @param filename - Optional filename for the downloaded file
   */
  const downloadPhoto = async (photoId: string, filename?: string): Promise<void> => {
    setIsDownloading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${apiUrl}/photos/${photoId}/download`, {
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header if not provided
      let downloadFilename = filename;
      if (!downloadFilename) {
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = filenameMatch[1];
          }
        }
      }

      // Fallback filename
      if (!downloadFilename) {
        downloadFilename = `photo-${photoId}.jpg`;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('ダウンロード完了');
    } catch (error) {
      console.error('Photo download error:', error);
      toast.error('ダウンロードに失敗しました');
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Download a shared photo by token
   * @param token - The share token for the photo
   * @param filename - Optional filename for the downloaded file
   */
  const downloadSharedPhoto = async (token: string, filename?: string): Promise<void> => {
    setIsDownloading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
      // /share/photo/:token/download is outside /api/v1
      const baseUrl = apiUrl.replace('/api/v1', '');
      const response = await fetch(`${baseUrl}/share/photo/${token}/download`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header if not provided
      let downloadFilename = filename;
      if (!downloadFilename) {
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = filenameMatch[1];
          }
        }
      }

      // Fallback filename
      if (!downloadFilename) {
        downloadFilename = `photo-${token}.jpg`;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('ダウンロード完了');
    } catch (error) {
      console.error('Shared photo download error:', error);
      toast.error('ダウンロードに失敗しました');
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadPhoto,
    downloadSharedPhoto,
    isDownloading,
  };
}
